import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const CHART_MARGIN = { top: 8, right: 45, bottom: 0, left: 0 };
const AXIS_TICK = { fontFamily: "monospace", fontSize: 10 };
const TOOLTIP_LABEL_STYLE = { fontFamily: "monospace", fontSize: 11 };
const TOOLTIP_CONTENT_STYLE = { border: "1px solid #0A0A0A", borderRadius: 0, fontFamily: "monospace", fontSize: 11 };
const LINE_DOT = { r: 4, fill: "#FFCC00", stroke: "#0A0A0A", strokeWidth: 1 };
const LINE_ACTIVE_DOT = { r: 6, fill: "#FF3B30" };
const fmtAxisEur = (v) => `€${v}`;

export const TrendChart = ({ items, catLabel }) => {
  const { t, lang } = useLang();
  const [selCat, setSelCat] = useState("");

  const trendCategories = [...new Set(items.map((v) => v.input.category))];
  const activeCat = selCat && trendCategories.includes(selCat) ? selCat : trendCategories[0];
  const trendData = items
    .filter((v) => v.input.category === activeCat)
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map((v) => ({
      date: new Date(v.created_at).toLocaleString(lang === "it" ? "it-IT" : "en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      value: Number(v.result.estimated_market_value),
    }));

  return (
    <section data-testid="trend-section" className="mt-10 border border-black p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-[#002FA7]" />
          <span className="overline">{t.dashboard.trend_title}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {trendCategories.map((c) => (
            <button
              key={c}
              data-testid={`trend-cat-${c}`}
              onClick={() => setSelCat(c)}
              className={`font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 border border-black transition-colors ${activeCat === c ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"}`}
            >
              {catLabel(c)}
            </button>
          ))}
        </div>
      </div>
      <div data-testid="trend-chart" className="mt-6 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#00000015" />
            <XAxis dataKey="date" tick={AXIS_TICK} stroke="#0A0A0A" />
            <YAxis tick={AXIS_TICK} stroke="#0A0A0A" tickFormatter={fmtAxisEur} width={70} />
            <Tooltip
              formatter={(v) => [`€${Number(v).toFixed(2)}`, t.results.market_value]}
              labelStyle={TOOLTIP_LABEL_STYLE}
              contentStyle={TOOLTIP_CONTENT_STYLE}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#002FA7"
              strokeWidth={2}
              dot={LINE_DOT}
              activeDot={LINE_ACTIVE_DOT}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {trendData.length < 2 && (
        <p data-testid="trend-hint" className="font-mono text-[10px] tracking-widest uppercase text-[#737373] mt-3">{t.dashboard.trend_empty}</p>
      )}
    </section>
  );
};

export default TrendChart;
